import { Formik } from 'formik';
import { pickBy } from 'lodash';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { getWAFFormConfig } from '@constants';
import { ApiEndpoint, FormType } from '@enums';
import { useAppDispatch, useFormError, useFormFilter } from '@hooks';
import { updateFormFilter, updateFormPages } from '@store';
import i18n from '@translations';
import type { OptionItem, WAFFilterForm, WAFFilterModalProps } from '@types';

export const WAFFilterModal: FC<WAFFilterModalProps> = ({ open, onClose, options = [] }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnWAF;
  const endpoint = ApiEndpoint.RULE;

  const filter = useFormFilter(formType);
  const submitError = useFormError({ formType, endpoint });

  const filterCriteria = filter?.filterCriteria ?? [];
  const getValue = (key: string) => filterCriteria.find((item: OptionItem) => item.label === key)?.value ?? '';

  const initialValues = {
    rule_name: getValue('rule_name'),
    group_id: getValue('group_id'),
    sensitivity: getValue('sensitivity'),
    rule_status: getValue('rule_status'),
  };

  const { label, inputList } = useMemo(
    () => getWAFFormConfig({ options, endpoint: ApiEndpoint.RULE }),
    [options, i18n.language],
  );

  const handleSubmit = (values: WAFFilterForm) => {
    const filteredValues = pickBy(values, (value) => value !== '');

    const criteriaArray = Object.entries({ application_id, ...filteredValues }).map(([label, value]) => ({
      label,
      value,
      from: endpoint,
    }));

    dispatch(
      updateFormFilter({
        formType,
        endpoint,
        value: criteriaArray,
      }),
    );
    dispatch(
      updateFormPages({
        formType,
        page: 1,
        params: { application_id, ...filteredValues },
        globalOptions: {
          pagination: true,
          filter: true,
          endpoint,
        },
      }),
    );

    if (!submitError) {
      onClose();
    }
  };

  const handleResetAll = (setValues: (values: WAFFilterForm) => void, values: WAFFilterForm) => {
    const resetValues = Object.fromEntries(Object.keys(values).map((key) => [key, '']));

    setValues(resetValues);
  };

  return (
    <Formik<WAFFilterForm> initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ values, touched, errors, setValues }) => (
        <FormModal
          formType={formType}
          title={label?.title}
          description={label?.description}
          onClose={onClose}
          onCancel={() => handleResetAll(setValues, values)}
          size="lg"
          open={open}
          confirmButton={{ children: label?.confirmButton }}
          cancelButton={{ children: label?.cancelButton }}
        >
          <GFormFields inputList={inputList} touched={touched} errors={errors} />
        </FormModal>
      )}
    </Formik>
  );
};
